import {DeleteObservationItem, ObservationData} from "../../observation.model";
import {HealthPlanActivitiesReport} from "../../health-plan.model";
import {DevicesData} from "../../devices-data.model";
import { Person } from "../../session-context.model";

export class SavePayload {
  readonly mode: string = "SAVE";
}

export class SavePartialPayload {
  readonly mode: string = "SAVE_PARTIAL";
}

export class DeletePayload {
  readonly mode: string = "DELETE";
}

export class SaveMessagePayload extends SavePayload {
  items: MessageToSend[];

  constructor(messageList: MessageToSend[]) {
    super();
    this.items = messageList;
  }
}

export class SaveObservationPayload extends SavePayload {
  items: Observation[];

  constructor(obsList: Observation[]) {
    super();
    this.items = obsList;
  }
}

export class SaveObservationDataPayload extends SavePayload {
  items: ObservationData[];

  constructor(obsList: ObservationData[]) {
    super();
    this.items = obsList;
  }
}

export class SavePersonWardPayload extends SavePayload {
  items: PersonWard[];

  constructor(personWardList: PersonWard[]) {
    super();
    this.items = personWardList;
  }
}

export class SaveActivityReportPayload extends SavePayload {
  items: HealthPlanActivitiesReport[];

  constructor(reportList: HealthPlanActivitiesReport[]) {
    super();
    this.items = reportList;
  }
}

export class DeleteActivityReportPayload extends DeletePayload {
  items: HealthPlanActivitiesReport[];

  constructor(reportList: HealthPlanActivitiesReport[]) {
    super();
    this.items = reportList;
  }
}

export class SaveMotivationPayload extends SavePayload {
  items: Motivation[];

  constructor(obsList: Motivation[]) {
    super();
    this.items = obsList;
  }
}

export class SaveBloodPressureDataObservationPayload extends SavePayload {
  items: any[];

  constructor(BloodPressureDataObservationList: any[]) {
    super();
    this.items = BloodPressureDataObservationList;
  }
}

export class DeleteObservations extends DeletePayload {
  items: DeleteObservationItem[];

  constructor(DeleteObservationList: DeleteObservationItem[]) {
    super();
    this.items = DeleteObservationList;
  }
}

export class SaveDeviceChangePayload extends SavePartialPayload {
  items: DevicesData[];

  constructor(DeviceDataList: DevicesData[]) {
    super();
    this.items = DeviceDataList;
  }
}

export class DeleteDevicePayload extends DeletePayload {
  items: DevicesData[];

  constructor(DeviceDataList: DevicesData[]) {
    super();
    this.items = DeviceDataList;
  }
}

export class MessageToSend {
  readonly type: string = "message";
  typeCodeId: number;
  subject: string;
  content: string;
  attachmentList: Attachment[];
  masterMessageId: number;
  observationId: number;
  personWardId: string;
  sentToPersonId: number;
  registerId: number;
  crossCommunicationStatus: boolean;
  prescriptionStatusId: number;

  constructor(
    code: number,
    subj: string,
    cont: string,
    attachm?: Attachment[],
    masterId?: number,
    obsId?: number,
    personWardId?: string,
    gpId?: number,
    registerId?: number,
    isCrossCommunication?: boolean,
    prescriptionStatusId?: number,
  ) {
    this.typeCodeId = code;
    this.subject = subj;
    this.content = cont;
    this.attachmentList = attachm;
    this.masterMessageId = masterId;
    this.observationId = obsId;
    this.personWardId = personWardId;
    this.sentToPersonId = gpId;
    this.registerId = registerId;
    this.crossCommunicationStatus = isCrossCommunication;
    this.prescriptionStatusId = prescriptionStatusId;
  }
}

export class Observation {
  readonly type: string = "observation";
  classificatorCodeId: number;
  value: number;
  observationList: Observation[];
  feelingCodeId?: number;
  valueTypeId?: number;
  valueUnitId?: number;
  effectiveTime?: string;

  constructor(code: number, obsList: Observation[], val: number, valTypeId?: number, valUnitId?: number, effTime?: string) {
    this.classificatorCodeId = code;
    this.observationList = obsList;
    this.value = val;
    this.valueTypeId = valTypeId;
    this.valueUnitId = valUnitId;
    this.effectiveTime = effTime;
  }
}

export class ObservationWithPerson extends Observation {
  representerPersonId: number;
  personId: number;

  constructor(code: number, obsList: Observation[], value: number, personId: number) {
    super(code, obsList, value);
    this.personId = personId;
  }
}

export class PersonWard {
  readonly type: string = "personWard";
  personId: number;
  wardFirstName: string;
  wardLastName: string;
  wardPersonalCode: string;

  constructor(
    personId: number,
    wardFirstName: string,
    wardLastName: string,
    wardPersonalCode: string,
  ) {
    this.personId = personId;
    this.wardFirstName = wardFirstName;
    this.wardLastName = wardLastName;
    this.wardPersonalCode = wardPersonalCode;
  }
}

export interface FullPersonWard extends PersonWard {
  id: string;
  insertTime: string;
  person: Person;
  personWard: PersonWard;
  updateTime: string;
}

export class Attachment {
  readonly type: string = "attachment";
  name: string;
  content: string;
  metadata: string;
  index: number;
  id: number;
  messageId: number;
  status: string;

  constructor(
    name: string,
    content: string,
    metadata: string,
    index: number,
    status?: string
  ) {
    this.name = name;
    this.content = content;
    this.metadata = metadata;
    this.index = index;
    this.status = status
  }
}

export interface GeneralRequestResponse {
  mode: string;
  type: string;
  items: any[];
  delete?: boolean;
  pagination?: any;
  acknowledgment: {
    acknowledgmentDetailList: any[];
    responseCode: string;
  };
}

export interface PaymentInfo {
  paymentStatus: string;
}

export interface SaveMessageResponse extends GeneralRequestResponse {
  items: SaveMessageResponseItem[];
}

export interface SaveMessageResponseItem {
  content: string;
  directionCodeId: number;
  id: number;
  insertTime: string;
  levelCodeId: number;
  observationId: number;
  personId: number;
  registerId: number;
  representerPersonId: number;
  sentByPersonId: number;
  sentToPersonId: number;
  statusCodeId: number;
  subject: string;
  type: string;
  typeCodeId: number;
}


export interface SymptomIds {
  pain: number;
  colds: number;
  bloodPressure: number;
  mood: number;
  fever: number;
  cough: number;
  indigestion: number;
  other: number;
}

export class Motivation {
  readonly type: string = "observation";
  classificatorCodeId: number;
  valueTypeId;
  valueUnitId: number;
  value;
  observationList: Motivation[];

  constructor(code: number,
              obsList: Motivation[],
              val: any,
              valueTypeId,
              valueUnitId: number,
  ) {
    this.classificatorCodeId = code;
    this.value = val;
    this.valueTypeId = valueTypeId;
    this.valueUnitId = valueUnitId;
    this.observationList = obsList;
  }
}
